import { t } from 'ttag'
import { useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'

import occupationsSVG from '@h2/assets/svg/icons/occupations.svg'
import { api } from '@h2/api'
import { user as getUser } from '@h2/common/selectors/user'
import { Loader } from '@h2/components/loading/loader'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Page } from '@h2/common/components/page'
import { PB97T, SDB, WHITE, PB90T } from '@h2/components/colors'
import { CollapseBottomWhitespace } from '@h2/common/components/layout'
import { Chip } from '@h2/components/display/chip'
import { Flex } from '@h2/components/layout/flex'
import { ClusterIcon, OccupationsIcon, VideoIcon, PrintIcon } from '@h2/components/icons'
import LmiBox from '@main/components/occupations/LmiBox'
import { Modal } from '@h2/components/containers/modal'
import { Button } from '@h2/components/buttons/button'
import {
  retrieveComparisonRegions,
  selectComparisonRegion
} from '@main/actions/occupationProfile'
import ComparisonRegionSelector from '@main/components/ComparisonRegionSelector'
import { Link } from '@h2/common/components/link'

import { Interests } from './components/interests'
import { WorkValues } from './components/workValues'
import { SimilarOccupations } from './components/similarOccupations'
import { IndustriesComprising } from './components/industriesComprising'
import { MediaSlideshow } from './components/mediaSlideshow'
import { WorkKeys } from './components/workKeys'
import { HowToPursue } from './components/howToPursue'

import { NextSteps } from '../../opportunities/components/nextSteps'

const PaddedWrap = styled.div`
  padding: 15px;
`
const Section = styled.div`
  background-color: #fff;
  padding: 15px;
  width: 100%;
  margin: 15px 0 15px 0;
`
const TitleSection = styled(Section)`
  background-color: ${SDB};
  margin-top: 0;
`
const TitleWrapper = styled.div`
  width: 100%;
`
const PageTitleWrapper = styled(TitleWrapper)`
  margin: -15px;
  min-height: 160px;
`
const SectionListContainer = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`
const SectionListItem = styled.li`
  list-style: none;
  padding: 0;
  margin: 0 0 15px 15px;
`
const StyledTitleImage = styled.div`
  display: flex;
  max-height: 200px;
  background-image: ${({ src }) => `url(${src})`};
  background-size: ${({ shouldContain }) => (shouldContain ? 'contain' : 'cover')};
  background-repeat: no-repeat;
  background-position: center;
  background-color: white;
  padding: 6px;
  width: 30%;
  max-width: 300px;
  min-height: 160px;
  align-items: flex-end;
`
const LmiBoxWrap = styled.div`
  background-color: ${WHITE} !important;
  & .lmi-nav {
    background-color: ${WHITE} !important;
  }
`
const TitleImage = ({ media, openModalFunction, onetSoc }) => {
  let awsUrl = media.find(item => item.mediaType === 'AWSPICTURE')

  if (!awsUrl) {
    awsUrl = media.find(item => item.mediaType === 'PICTURE')
  }

  const pic = awsUrl ? awsUrl.url : occupationsSVG

  return (
    <StyledTitleImage src={pic} shouldContain={!awsUrl}>
      <Flex gap="5px">
        {media.length !== 0 && (
          <Button
            align="right"
            disabled={media.length === 0}
            onClick={openModalFunction}
            size="small"
            variant="outlined"
            icon={VideoIcon}>
            <span style={{ whiteSpace: 'nowrap' }}>{t`View Gallery`}</span>
          </Button>
        )}
        <Link
          ssr={true}
          align="right"
          size="small"
          variant="outlined"
          href={`/pdfs/profile/${onetSoc}`}
          icon={PrintIcon}>
          <span style={{ whiteSpace: 'nowrap' }}>{t`Save PDF`}</span>
        </Link>
      </Flex>
    </StyledTitleImage>
  )
}
const OccupationDetail = () => {
  const { onetSoc } = useParams()
  const userInfo = useSelector(getUser)
  const [isOpen, setIsOpen] = useState(false)
  const [regionSelectorOpen, setRegionSelectorOpen] = useState(false)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const {
    data: occupationDetails,
    isError,
    isLoading
  } = useQuery(['occupation-detail', onetSoc], () => {
    return api.fetch(`/occupation/${onetSoc}`, {
      method: 'GET'
    })
  })

  if (isLoading) {
    return <Loader />
  }

  if (!occupationDetails || isError) {
    return (
      <Page bgColor={PB97T}>
        <Page.Layout>
          <Page.Content>
            <Heading level="2">{t`Could not locate occupation ${onetSoc}`}</Heading>
          </Page.Content>
        </Page.Layout>
      </Page>
    )
  }

  const openModal = () => {
    setIsOpen(true)
  }
  const closeModal = () => {
    setIsOpen(false)
  }
  const OutputSection = ({ value, title }) => {
    if (!value) {
      return null
    }

    return (
      <Section>
        <TitleWrapper>
          <Heading level="4" fontSize="30px">
            {title}
          </Heading>
          {value}
        </TitleWrapper>
      </Section>
    )
  }
  const outputTasks = () => {
    if (!occupationDetails.tasks) {
      return null
    }

    return (
      <SectionListContainer>
        {occupationDetails.tasks.slice(0, 5).map((task, taskPos) => (
          <SectionListItem key={taskPos}>{task}</SectionListItem>
        ))}
      </SectionListContainer>
    )
  }
  const alsoCalled = () => {
    if (!occupationDetails.layTitles) {
      return null
    }

    return (
      <Flex gap="10px" wrap={'wrap'}>
        {occupationDetails.layTitles.slice(0, 5).map((task, taskPos) => (
          <Chip
            align="right"
            color="gray"
            margin="5px 5px 5px 5px"
            key={taskPos}
            size="large"
            variant="contained">
            {task}
          </Chip>
        ))}
      </Flex>
    )
  }
  const outputElements = sectionKey => {
    if (!occupationDetails[sectionKey]) {
      return null
    }

    return (
      <SectionListContainer>
        {occupationDetails[sectionKey].slice(0, 5).map(itm => (
          <SectionListItem key={itm.id}>
            <Text weight="bold" as="span">
              {itm.name}
            </Text>
            <Text as="span">
              {' - '}
              {itm.description}
            </Text>
          </SectionListItem>
        ))}
      </SectionListContainer>
    )
  }
  const similarOccupationSection = (
    <SimilarOccupations
      showMilitary={dynamicValues.includeMilitaryCustomizations}
      currentOccupation={occupationDetails}
      relatedOnet={occupationDetails?.related}
      relatedMilitary={occupationDetails?.militaryEquivalent}
      occupationMeta={occupationDetails?.occupationMeta || {}}
    />
  )

  return (
    <>
      <Page bgColor={PB97T}>
        <Page.Layout>
          <Page.Content>
            <TitleSection>
              <PageTitleWrapper>
                <Flex gap="25px">
                  <TitleImage
                    media={occupationDetails.media || []}
                    openModalFunction={openModal}
                    onetSoc={onetSoc}
                  />
                  <div style={{ padding: '15px' }}>
                    <Heading level="2" fontSize="30px" color={WHITE}>
                      {occupationDetails.title}
                    </Heading>
                    <Flex gap="5px" wrap="wrap">
                      <Chip icon={OccupationsIcon} size="large">
                        <Text as="span">{t`Occupation`}</Text>
                      </Chip>
                      {occupationDetails.cluster && (
                        <Chip icon={ClusterIcon} size="large">
                          <Link href={occupationDetails.cluster.url}>
                            <Text as="span">{occupationDetails.cluster.name}</Text>
                          </Link>
                        </Chip>
                      )}
                      {occupationDetails.occupationMeta?.flags[onetSoc]?.map(flag => (
                        <Chip key={flag.id} size="large">
                          <Text as="span">{flag.name}</Text>
                        </Chip>
                      ))}
                    </Flex>
                  </div>
                </Flex>
              </PageTitleWrapper>
            </TitleSection>
            <OutputSection
              title={t`About the job`}
              value={<Text>{occupationDetails.description}</Text>}
            />
            <div id="profileinfocontainer">
              <div className="headerBox" style={{ border: 'none' }}>
                <LmiBoxWrap className="lmi-callout">
                  <LmiBox
                    profile={occupationDetails}
                    openRegionsModal={async () => {
                      await dispatch(retrieveComparisonRegions())
                      setRegionSelectorOpen(true)
                    }}
                  />
                </LmiBoxWrap>
              </div>
            </div>

            <OutputSection title={t`What they do`} value={outputTasks()} />
            <HowToPursue
              title={t`How to pursue this`}
              recommendations={occupationDetails.recommendations}
            />
            <Flex gap="15px">
              <Interests interests={occupationDetails?.interests} />
              <WorkValues workValues={occupationDetails?.workValues} />
            </Flex>
            <OutputSection
              title={t`Things they need to know`}
              value={outputElements('knowledge')}
            />
            <OutputSection
              title={t`Things they need to be able to do`}
              value={outputElements('skills')}
            />

            <Flex gap="15px">
              <OutputSection
                title={t`Industries Employing`}
                value={<IndustriesComprising occupation={occupationDetails} />}
              />

              {occupationDetails.workKeys &&
                occupationDetails.workKeys.skills.length &&
                dynamicValues.workKeys && (
                  <OutputSection
                    title={t`Work Keys`}
                    value={<WorkKeys occupation={occupationDetails} />}
                  />
                )}
            </Flex>
            <OutputSection
              title={t`Similar Occupations`}
              value={similarOccupationSection}
            />
            <OutputSection title={t`It is also called`} value={alsoCalled()} />
          </Page.Content>
          <Page.Sidebar bgColor={PB90T}>
            <PaddedWrap>
              <NextSteps
                objectId={occupationDetails.onetSoc}
                objectType={'onet'}
                user={userInfo}
                recommendations={occupationDetails.recommendations}
              />
            </PaddedWrap>
          </Page.Sidebar>
        </Page.Layout>
      </Page>
      <Modal isOpen={regionSelectorOpen} onClose={() => setRegionSelectorOpen(false)}>
        <Modal.Header>
          <Heading level="2">{t`Choose a region`}</Heading>
        </Modal.Header>
        <Modal.Body>
          <ComparisonRegionSelector
            onSelectRegion={async region => {
              const comparisonResponse = await dispatch(
                selectComparisonRegion(occupationDetails, region)
              )

              queryClient.setQueryData(['occupation-detail', onetSoc], oldProfile => {
                return {
                  ...oldProfile,
                  comparisonWages: comparisonResponse.wages,
                  comparisonOutlook: comparisonResponse.outlook
                }
              })
              setRegionSelectorOpen(false)
            }}
          />
        </Modal.Body>
      </Modal>
      <Modal isOpen={isOpen} closeOnClickOverlay={true} onClose={closeModal}>
        <Modal.Header>
          <Heading level="2">{t`Media Gallery for ${occupationDetails.title}`}</Heading>
        </Modal.Header>
        <Modal.Body>
          <MediaSlideshow
            onetSoc={occupationDetails.onetSoc}
            media={(occupationDetails.media || []).sort(fileA =>
              fileA.mediaType === 'VIMEO' ? 0 : 1
            )}
          />
        </Modal.Body>
      </Modal>
      <CollapseBottomWhitespace />
    </>
  )
}

export { OccupationDetail }
